<template>
  <!-- Lienzo del mapeo: dos columnas alineadas fila a fila (preguntas a la izquierda, la entidad a la
       derecha) y una línea por conexión. Arrastrar el punto ● de una pregunta hasta un campo conecta;
       pulsar un campo abre su hoja; pulsar una línea abre su menú. Rueda = desplazar, Ctrl+rueda = zoom. -->
  <div ref="wrapRef" class="mc-wrap" :class="{ panning, lensing: !!lens && !hl }"
       @pointerdown="onWrapDown" @wheel.prevent="onWheel">
    <div ref="worldRef" class="mc-world">
      <svg class="mc-edges" :class="{ hovering: !!hl }" :width="WORLD.w" :height="WORLD.h">
        <TransitionGroup tag="g" name="vem-edge">
          <path v-for="e in paths" :key="e.key" :d="e.d" :style="{ d: `path('${e.d}')` }"
                :class="['mc-edge', e.kind, { hl: hl && hl.edges.has(e.key), lens: lens && lens.edges.has(e.key) }]" />
        </TransitionGroup>
        <g class="mc-hits">
          <path v-for="e in paths" :key="'h' + e.key" :d="e.d" class="mc-hit"
                @pointerenter="hoverEdge = e.key" @pointerleave="hoverEdge = null"
                @click.stop="menuAt(e, $event)" @contextmenu.prevent.stop="menuAt(e, $event)" />
        </g>
        <g v-if="handle" class="mc-handle-g">
          <circle :cx="handle.m.x" :cy="handle.m.y" r="9" class="mc-handle" />
          <text :x="handle.m.x" :y="handle.m.y" class="mc-handle-t">⋯</text>
        </g>
        <path v-if="conn.active" :d="ghostPath" class="mc-edge ghost" />
      </svg>

      <div v-for="n in nodes" :key="n.key" class="mc-node" :class="[n.kind, { missing: n.missing }]"
           :style="{ left: n.x + 'px', top: n.y + 'px', width: n.w + 'px' }"
           :tabindex="n.kind === 'entity' ? 0 : null" :role="n.kind === 'entity' ? 'listbox' : null"
           :aria-label="n.kind === 'entity' ? n.title : null"
           :aria-activedescendant="n.kind === 'entity' && active ? rowDomId(active) : null"
           @keydown="n.kind === 'entity' && onEntityKey($event, n)" @blur="n.kind === 'entity' && (kbd = false)">
        <div class="mc-head">
          <span v-if="n.kind === 'entity'" class="mc-ico entity">E</span>
          <span class="mc-titles">
            <span class="mc-title" :title="n.title">{{ n.title }}</span>
            <span class="mc-sub" :title="n.sub">{{ n.sub }}</span>
          </span>
        </div>
        <TransitionGroup tag="div" name="vem-row" class="mc-rows">
          <template v-for="r in n.rows" :key="r.id">
            <div v-if="r.kind === 'gap'" class="mc-row gap" />
            <div v-else-if="r.kind === 'section'" class="mc-row section" :class="{ legacy: r.legacy, sub: r.sub }">
              <span class="mc-label">{{ r.label }}</span><span v-if="r.count" class="mc-count">{{ r.count }}</span>
            </div>
            <button v-else-if="r.kind === 'group'" :id="rowDomId(r.id)" type="button" class="mc-row group"
                    :class="{ kbd: kbd && active === r.id }" :aria-expanded="r.open" @click="$emit('toggle-group', r.id)">
              <span class="mc-caret">{{ r.open ? '▾' : '▸' }}</span>{{ r.label }}
            </button>
            <button v-else-if="r.kind === 'action'" type="button" class="mc-row action" :title="r.title" @click="$emit('action', r.id)">{{ r.label }}</button>
            <div v-else-if="r.kind === 'pill'" class="mc-row pill" :data-row="n.key + '|' + r.id"
                 :class="{ sys: r.sys, dead: r.dead, free: !r.connected, hl: hl && hl.rows.has(n.key + '|' + r.id),
                           lens: lens && lens.rows.has(n.key + '|' + r.id), dim: r.dim, lit: r.lit }"
                 :title="r.title" @pointerenter="enterRow(n, r)" @pointerleave="leaveRow">
              <span v-for="tg in r.tags" :key="tg" class="mc-tag" :class="r.sys ? 'sys' : 'key'">{{ tg }}</span>
              <span v-if="r.glyph" class="mc-glyph" :title="r.glyphTitle">{{ r.glyph }}</span>
              <span class="mc-label">{{ r.label }}</span>
              <span v-if="!r.sys && r.cov.dots" class="mc-cov">
                <i v-for="(d, i) in r.cov.dots" :key="i" :class="d.state" :style="{ '--fc': d.color }" :title="d.title">{{ d.code }}</i>
              </span>
              <span v-else-if="!r.sys && r.cov.N" class="mc-covk" :title="r.title">
                {{ r.cov.k }}/{{ r.cov.N }}<span class="mc-bar"><b :style="{ width: (100 * r.cov.k / r.cov.N) + '%' }" /></span>
              </span>
              <span class="mc-port out" :class="{ on: r.connected }" @pointerdown.stop="onPortDown($event, n, r)" />
            </div>
            <div v-else :id="rowDomId(r.id)" class="mc-row clickable" :data-row="n.key + '|' + r.id"
                 :data-target="r.kind === 'row' ? r.id : null" role="option" :aria-selected="active === r.id"
                 :class="[r.kind, r.status, { on: r.connected, other: r.other, warnk: r.warn, hl: hl && hl.rows.has(n.key + '|' + r.id),
                           lens: lens && lens.rows.has(n.key + '|' + r.id), dim: r.dim, lit: r.lit, kbd: kbd && active === r.id }]"
                 :title="r.title" @pointerenter="enterRow(n, r)" @pointerleave="leaveRow" @click="open(r)">
              <span v-if="r.kind === 'row'" class="mc-port tgt" />
              <template v-if="r.kind === 'key'">
                <span class="mc-keytag">{{ t(r.role === 'id' ? 'chip.keyId' : r.role === 'entity' ? 'src.keyEntity' : 'chip.keyOrigin') }}</span>
                <span class="mc-label">{{ r.label }}</span>
                <span class="mc-keyval" :class="{ warn: r.warn }">← {{ r.value }}</span>
              </template>
              <template v-else>
                <span class="mc-glyph" :title="r.glyphTitle">{{ r.glyph }}</span>
                <span class="mc-label">{{ r.label }}</span>
                <span v-for="c in r.chips" :key="c.kind + c.text" class="mc-chip" :class="c.kind" :title="c.title">{{ c.text }}</span>
                <span v-if="r.status === 'warn' || r.status === 'danger'" class="mc-st" :class="r.status" />
              </template>
            </div>
          </template>
        </TransitionGroup>
      </div>
    </div>

    <div class="mc-zoom">
      <button type="button" aria-label="-" @pointerdown.stop @click="zoomBy(1 / 1.2)">–</button>
      <button ref="pctRef" type="button" class="pct" :title="t('zoom.fitWidth')" @pointerdown.stop @click="fitWidth" />
      <button type="button" aria-label="+" @pointerdown.stop @click="zoomBy(1.2)">+</button>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watchEffect } from 'vue';
import { t } from '@vem/i18n.js';
import { bezier, bezierPoint, HEADER_H, ROW_H } from '@vem/layout.js';

const props = defineProps({
  nodes: { type: Array, required: true },
  edges: { type: Array, required: true },
  /** Recorrido fijo (hoja o menú abiertos): { rows:Set('nodo|fila'), edges:Set(clave) } */
  focus: { type: Object, default: null },
  /** Lente de un formulario del carril (mismo formato). */
  lens: { type: Object, default: null },
});
const emit = defineEmits(['connect', 'open-field', 'edge-menu', 'toggle-group', 'hover-row', 'drag', 'action']);

const WORLD = { w: 4000, h: 6000 };
const EDGE_PAD = 40;
const TOOLS_H = 50; // hueco para la barra de búsqueda/avisos que flota sobre el lienzo
const wrapRef = ref(null);
const worldRef = ref(null);
const pctRef = ref(null);
const pan = reactive({ x: 0, y: 0 });
const zoom = ref(1);
const panning = ref(false);

// Desplazar y hacer zoom van directos al DOM, fuera del render: si re-renderizaran el lienzo, los
// <TransitionGroup> de filas y líneas verían todo «movido» (el mundo se ha trasladado) y lo animarían a la zaga.
// Síncrono: cualquier render posterior en el mismo tick ya mide con el desplazamiento aplicado.
watchEffect(() => {
  if (!wrapRef.value || !worldRef.value) return;
  wrapRef.value.style.backgroundPosition = `${pan.x}px ${pan.y}px`;
  wrapRef.value.style.backgroundSize = `${22 * zoom.value}px ${22 * zoom.value}px`;
  worldRef.value.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom.value})`;
  if (pctRef.value) pctRef.value.textContent = `${Math.round(zoom.value * 100)}%`;
}, { flush: 'sync' });

const conn = reactive({ active: false, alias: null, x1: 0, y1: 0, x2: 0, y2: 0, cx: 0, cy: 0 });
const hoverRow = ref(null); // «nodo|fila»
const hoverEdge = ref(null);
const active = ref(null); // fila activa de la caja de la entidad (teclado)
const kbd = ref(false);
let drag = null;
let raf = 0;

const rowDomId = (id) => 'mcr-' + String(id).replace(/[^A-Za-z0-9_-]/g, '_');

const index = computed(() => {
  const m = new Map();
  for (const n of props.nodes) n.rows.forEach((r, i) => m.set(n.key + '|' + r.id, { n, i }));
  return m;
});

function portPos(node, rowId, side) {
  const hit = index.value.get(node + '|' + rowId);
  if (!hit) return null;
  return { x: side === 'out' ? hit.n.x + hit.n.w : hit.n.x, y: hit.n.y + HEADER_H + hit.i * ROW_H + ROW_H / 2 };
}

const paths = computed(() => props.edges.flatMap((e) => {
  const a = portPos(e.from.node, e.from.row, 'out');
  const b = portPos(e.to.node, e.to.row, 'in');
  if (!a || !b) return [];
  return [{ ...e, a, b, d: bezier(a, b) }];
}));

const handle = computed(() => {
  const e = hoverEdge.value && paths.value.find((p) => p.key === hoverEdge.value);
  return e ? { ...e, m: bezierPoint(e.a, e.b, 0.5) } : null;
});

/** Lo iluminado: la arista o la fila bajo el puntero (solo su recorrido y sus extremos) o, si no, el foco fijo. */
const hl = computed(() => {
  if (hoverEdge.value) {
    const e = props.edges.find((x) => x.key === hoverEdge.value);
    if (e) return { edges: new Set([e.key]), rows: new Set([e.from.node + '|' + e.from.row, e.to.node + '|' + e.to.row]) };
  }
  if (hoverRow.value) {
    const edges = new Set();
    const rows = new Set([hoverRow.value]);
    for (const e of props.edges) {
      const from = e.from.node + '|' + e.from.row;
      const to = e.to.node + '|' + e.to.row;
      if (from === hoverRow.value || to === hoverRow.value) {
        edges.add(e.key);
        rows.add(from).add(to);
      }
    }
    return { edges, rows };
  }
  return props.focus;
});

const ghostPath = computed(() => bezier({ x: conn.x1, y: conn.y1 }, { x: conn.x2, y: conn.y2 }));

function enterRow(n, r) {
  hoverRow.value = n.key + '|' + r.id;
  emit('hover-row', { id: r.id, kind: r.kind, node: n.key });
}

function leaveRow() {
  hoverRow.value = null;
  emit('hover-row', null);
}

function open(r) {
  if (r.kind === 'row' || r.kind === 'key') emit('open-field', r.id);
  active.value = r.id;
}

function toWorld(clientX, clientY) {
  const r = wrapRef.value.getBoundingClientRect();
  return { x: (clientX - r.left - pan.x) / zoom.value, y: (clientY - r.top - pan.y) / zoom.value };
}

function toClient(p) {
  const r = wrapRef.value.getBoundingClientRect();
  return { x: r.left + pan.x + p.x * zoom.value, y: r.top + pan.y + p.y * zoom.value };
}

function menuAt(e, ev) {
  const p = ev && ev.type === 'contextmenu' ? { x: ev.clientX, y: ev.clientY } : toClient(bezierPoint(e.a, e.b, 0.5));
  emit('edge-menu', { index: e.index, x: p.x, y: p.y });
}

/** Desplaza la vista mientras se arrastra una conexión cerca del borde. */
function autoScroll() {
  raf = 0;
  if (!drag || drag.type !== 'conn') return;
  const r = wrapRef.value.getBoundingClientRect();
  let dx = 0;
  let dy = 0;
  if (conn.cx < r.left + EDGE_PAD) dx = 12;
  else if (conn.cx > r.right - EDGE_PAD) dx = -12;
  if (conn.cy < r.top + EDGE_PAD) dy = 12;
  else if (conn.cy > r.bottom - EDGE_PAD) dy = -12;
  if (dx || dy) {
    pan.x += dx;
    pan.y += dy;
    const p = toWorld(conn.cx, conn.cy);
    conn.x2 = p.x;
    conn.y2 = p.y;
    raf = requestAnimationFrame(autoScroll);
  }
}

function onMove(e) {
  if (!drag) return;
  if (drag.type === 'pan') {
    pan.x = drag.x0 + (e.clientX - drag.cx);
    pan.y = drag.y0 + (e.clientY - drag.cy);
  } else if (drag.type === 'conn') {
    conn.cx = e.clientX;
    conn.cy = e.clientY;
    const p = toWorld(e.clientX, e.clientY);
    conn.x2 = p.x;
    conn.y2 = p.y;
    if (!raf) raf = requestAnimationFrame(autoScroll);
  }
}

function onUp(e) {
  if (drag && drag.type === 'conn') {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const target = el && el.closest('[data-target]');
    if (target) emit('connect', { alias: conn.alias, target: target.getAttribute('data-target') });
    conn.active = false;
    emit('drag', false);
  }
  drag = null;
  panning.value = false;
  window.removeEventListener('pointermove', onMove);
  window.removeEventListener('pointerup', onUp);
}

function start(d) {
  drag = d;
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
}

function onPortDown(e, n, r) {
  if (e.button !== 0) return;
  e.preventDefault();
  const p = portPos(n.key, r.id, 'out');
  Object.assign(conn, { active: true, alias: r.id, x1: p.x, y1: p.y, x2: p.x, y2: p.y, cx: e.clientX, cy: e.clientY });
  start({ type: 'conn' });
  emit('drag', true);
}

function onWrapDown(e) {
  if (e.button !== 0 || e.target.closest('.mc-node, .mc-zoom, .mc-hit')) return;
  e.preventDefault();
  panning.value = true;
  start({ type: 'pan', x0: pan.x, y0: pan.y, cx: e.clientX, cy: e.clientY });
}

function zoomAt(factor, clientX, clientY) {
  const r = wrapRef.value.getBoundingClientRect();
  const before = toWorld(clientX, clientY);
  zoom.value = Math.min(2, Math.max(0.3, zoom.value * factor));
  pan.x = clientX - r.left - before.x * zoom.value;
  pan.y = clientY - r.top - before.y * zoom.value;
}

/** Límites del contenido (para no perderlo de vista al desplazar). */
function contentBox() {
  const xs = props.nodes.map((n) => n.x);
  const ys = props.nodes.map((n) => n.y);
  const maxX = Math.max(...props.nodes.map((n) => n.x + n.w));
  const maxY = Math.max(...props.nodes.map((n) => n.y + HEADER_H + n.rows.length * ROW_H));
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX, maxY };
}

function clampPan() {
  if (!props.nodes.length || !wrapRef.value) return;
  const r = wrapRef.value.getBoundingClientRect();
  const b = contentBox();
  const keep = 80;
  pan.x = Math.min(r.width - keep - b.minX * zoom.value, Math.max(keep - b.maxX * zoom.value, pan.x));
  pan.y = Math.min(r.height - keep - b.minY * zoom.value, Math.max(keep - b.maxY * zoom.value, pan.y));
}

function onWheel(e) {
  if (e.ctrlKey || e.metaKey) {
    zoomAt(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX, e.clientY);
    return;
  }
  pan.x -= e.deltaX;
  pan.y -= e.deltaY;
  clampPan();
}

function zoomBy(f) {
  const r = wrapRef.value.getBoundingClientRect();
  zoomAt(f, r.left + r.width / 2, r.top + r.height / 2);
}

/** Ajusta al ancho: todo el contenido a lo ancho, empezando arriba. */
function fitWidth() {
  if (!props.nodes.length || !wrapRef.value) return;
  const r = wrapRef.value.getBoundingClientRect();
  const b = contentBox();
  zoom.value = Math.min(1, Math.max(0.5, (r.width - 40) / (b.maxX - b.minX)));
  pan.x = 20 - b.minX * zoom.value;
  pan.y = TOOLS_H - b.minY * zoom.value;
}

// ── Teclado en la caja de la entidad ──────────────────────────────────────────
const entityNode = () => props.nodes.find((n) => n.kind === 'entity');
const navRows = () => (entityNode()?.rows || []).filter((r) => ['row', 'key', 'group'].includes(r.kind));

/** Lleva una fila a la vista y la marca como activa. */
function revealRow(id) {
  const n = entityNode();
  const hit = n && index.value.get(n.key + '|' + id);
  if (!hit || !wrapRef.value) return;
  const r = wrapRef.value.getBoundingClientRect();
  const y = (n.y + HEADER_H + hit.i * ROW_H) * zoom.value + pan.y;
  if (y < 50) pan.y += 50 - y;
  else if (y > r.height - 60) pan.y -= y - (r.height - 60);
  active.value = id;
}

function onEntityKey(e, n) {
  const rows = navRows();
  if (!rows.length) return;
  let i = rows.findIndex((r) => r.id === active.value);
  const move = (k) => {
    e.preventDefault();
    kbd.value = true;
    revealRow(rows[Math.max(0, Math.min(rows.length - 1, k))].id);
  };
  if (e.key === 'ArrowDown') move(i + 1);
  else if (e.key === 'ArrowUp') move(i < 0 ? 0 : i - 1);
  else if (e.key === 'Home') move(0);
  else if (e.key === 'End') move(rows.length - 1);
  else if ((e.key === 'Enter' || e.key === ' ') && i >= 0) {
    e.preventDefault();
    const r = rows[i];
    if (r.kind === 'group') emit('toggle-group', r.id);
    else if (e.key === 'Enter') emit('open-field', r.id);
  } else if ((e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) && i >= 0) {
    e.preventDefault();
    const edge = paths.value.find((p) => p.to.row === rows[i].id);
    if (edge) menuAt(edge);
  }
}

/** Enfoca la caja de la entidad con una fila activa (al cerrar la hoja o el menú). */
function focusRow(id) {
  revealRow(id);
  kbd.value = true;
  wrapRef.value?.querySelector('.mc-node.entity')?.focus({ preventScroll: true });
}

/** Rectángulo en pantalla de una fila de la entidad (origen de la animación de la hoja). */
function rowRect(id) {
  const el = wrapRef.value?.querySelector(`[data-row="entity|${CSS.escape(id)}"]`);
  return el ? el.getBoundingClientRect() : null;
}

onBeforeUnmount(() => {
  onUp({ clientX: 0, clientY: 0 });
  if (raf) cancelAnimationFrame(raf);
});

defineExpose({ fitWidth, focusRow, rowRect });
</script>
